/**
 * DetailSettingsSection.tsx
 * 請求書リンク, メール送信フラグ, JSONデータボタン, 予約日, 予約回数
 */
import React from 'react';
import { SectionProps } from '../types';
import SectionHeader from '../shared/SectionHeader';
import FieldDisplay from '../shared/FieldDisplay';
import { isoToLocalInput } from '../../../lib/dateUtils';

interface DetailSettingsSectionProps extends SectionProps {
  onInvoiceOpen?: () => void;
  onInvoiceCreate?: () => void;
  onInvoiceVoid?: () => void;
  onJsonModalOpen?: () => void;
  isCreatingInvoice?: boolean;
  isVoidingInvoice?: boolean;
}

export default function DetailSettingsSection({
  data,
  onChange,
  disabled,
  isNew,
  onInvoiceOpen,
  onInvoiceCreate,
  onInvoiceVoid,
  onJsonModalOpen,
  isCreatingInvoice,
  isVoidingInvoice,
}: DetailSettingsSectionProps) {
  const bookingDateDisplay = (isoToLocalInput(data.booking_date) || '').replace('T', ' ') || '-';

  return (
    <>
      <SectionHeader title="詳細設定" />

      {/* 請求書リンク (hidden when isNew) */}
      {!isNew && (
        <div className="gc-field">
          <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>
            請求書 <span style={{ fontWeight: 400, marginLeft: 4, opacity: 0.7 }}>
              ({data.days || 0}日{data.nights || 0}泊)
            </span>
          </label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            {data.invoice_link && onInvoiceOpen && (
              <button
                type="button"
                onClick={onInvoiceOpen}
                style={{
                  padding: '4px 12px',
                  fontSize: '0.75rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: 4,
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                Squareで開く
              </button>
            )}
            {data.invoice_link ? (
              onInvoiceVoid && (
                <button
                  type="button"
                  onClick={onInvoiceVoid}
                  disabled={disabled || isVoidingInvoice}
                  style={{
                    padding: '4px 12px',
                    fontSize: '0.75rem',
                    border: '1px solid #ef4444',
                    borderRadius: 4,
                    background: '#fff',
                    color: '#ef4444',
                    cursor: disabled ? 'default' : 'pointer',
                  }}
                >
                  取消
                </button>
              )
            ) : (
              onInvoiceCreate && (
                <button
                  type="button"
                  onClick={onInvoiceCreate}
                  disabled={disabled || isCreatingInvoice}
                  style={{
                    padding: '4px 12px',
                    fontSize: '0.75rem',
                    border: 'none',
                    borderRadius: 4,
                    background: '#3b82f6',
                    color: '#fff',
                    cursor: disabled ? 'default' : 'pointer',
                  }}
                >
                  発行
                </button>
              )
            )}
          </div>
          <input
            type="text"
            value={data.invoice_link || ''}
            onChange={(e) => onChange?.({ invoice_link: e.target.value })}
            disabled={disabled}
            placeholder="invoice ID（例: sq:0:inv:0-xxx）"
            style={{
              width: '100%',
              padding: '4px 8px',
              fontSize: '0.75rem',
              border: '1px solid #e2e8f0',
              borderRadius: 4,
              color: '#64748b',
              background: disabled ? '#f8fafc' : '#fff',
            }}
          />
        </div>
      )}

      {/* メール送信フラグ (hidden when isNew) */}
      {!isNew && (
        <div className="gc-field">
          <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>
            メール送信フラグ
          </label>
          <div style={{ display: 'flex', gap: 16, padding: '4px 0' }}>
            {[
              { field: 'mail_send_flg1', label: '予約確定' },
              { field: 'mail_send_flg2', label: '決済御礼' },
              { field: 'mail_send_flg3', label: '利用御礼' },
            ].map((flg) => (
              <label key={flg.field} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: disabled ? 'default' : 'pointer', fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={!!data[flg.field]}
                  onChange={(e) => onChange?.({ [flg.field]: e.target.checked })}
                  disabled={disabled}
                  style={{ width: 16, height: 16 }}
                />
                {flg.label}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* JSONデータボタン */}
      {!isNew && onJsonModalOpen && (
        <div style={{ padding: '4px 0' }}>
          <button
            type="button"
            onClick={onJsonModalOpen}
            disabled={disabled}
            style={{
              padding: '4px 12px',
              fontSize: '0.75rem',
              border: '1px solid #cbd5e1',
              borderRadius: 4,
              background: '#fff',
              color: '#475569',
              cursor: disabled ? 'default' : 'pointer',
            }}
          >
            その他データ
          </button>
        </div>
      )}

      {/* 事前決済リンク (read-only) */}
      {data.id && (
        <FieldDisplay
          label="事前決済リンク"
          value={`https://n-car.jp/sys/form/?id=${data.id}`}
        />
      )}

      {/* 予約日 (read-only) */}
      <FieldDisplay label="予約日" value={bookingDateDisplay} />

      {/* 予約回数 (read-only) */}
      <FieldDisplay
        label="来店回数"
        value={data.past_reservation_count ?? 0}
        suffix="回"
      />
    </>
  );
}
